<template>
  <modal
    v-if="show"
    :extra-wide="true"
    :title="i18n.t('/LeaseCompany/Search/Title')"
    @close="close"
  >
    <lease-company-search-box :info-text="infoText" @on-search="onSearch" />
    <alert v-if="errorText" :text="errorText" />
    <p />
    <div v-if="step > 0">
      <ul v-if="foundCompanies.length">
        <li v-for="(company, i) in companiesToShow" :key="i">
          <lease-company-card :company="company" @select-result="selectCompany" />
        </li>
      </ul>
      <loader v-if="loading" />
      <div v-if="showNoResultsMessage">
        <p>{{ i18n.t('/LeaseCompany/Search/NoResults') }}</p>
      </div>

      <app-button
        v-if="(step == 1 || step >= 3) && amountToShow <= foundCompanies.length"
        data-qa="show-more-lease-companies-button"
        @click="showMoreResults()"
      >
        {{ i18n.t('/LeaseCompany/Search/ShowMore') }}
      </app-button>
    </div>
  </modal>
</template>

<script>
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import Companies from '@/api/server/Companies'
import LeaseCompanyCard from '@/components/generic/presentational/molecules/leaseCompanyCard/LeaseCompanyCard.vue'
import LeaseCompanySearchBox from '@/components/generic/presentational/molecules/leaseCompanySearch/LeaseCompanySearchBox.vue'
import StoreConnector from '@/helpers/StoreConnector'
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'
import { EventBus } from '@/plugins/event-bus'
import { mapGetters } from 'vuex'
import CustomerSearchHelper from '@/helpers/CustomerSearchHelper'
import Alert from '@/components/generic/presentational/atoms/alert/Alert.vue'
import leaseReferenceHelper from '@/helpers/leaseReferenceHelper'

export default {
  components: {
    Modal,
    LeaseCompanyCard,
    LeaseCompanySearchBox,
    AppButton,
    Loader,
    Alert,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    isPurchaseOrderPage: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close'],
  data() {
    return {
      foundCompanies: [],
      loading: false,
      hasSearched: false,
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      step: 0,
      amountToShow: 20,
      errorText: '',
    }
  },
  computed: {
    ...mapGetters({
      dealerSettings: 'user/dealerSettings',
      customer: 'quote/customer',
      customerCompany: 'quote/customerCompany',
    }),
    showNoResultsMessage() {
      return this.foundCompanies.length == 0 && this.hasSearched && !this.loading
    },
    companiesToShow() {
      return this.foundCompanies.filter((company, index) => index < this.amountToShow)
    },
    infoText() {
      if (!this.dealerSettings.autolineKeyloopEnabled) {
        return this.i18n.t('/LeaseCompany/Search/InfoNational')
      }
      return this.i18n.t('/LeaseCompany/Search/Info')
    },
  },
  methods: {
    close() {
      this.step = 0
      this.foundCompanies = []

      this.$emit('close')
    },
    getSearchNationalParams(query) {
      let searchParams = null

      const poIdOrKVKNumber = CustomerSearchHelper.filterPoIdOrKvkNumber(query)
      const kvkLocationNumber = CustomerSearchHelper.filterKvkLocationNumber(query)
      const zipcode = CustomerSearchHelper.filterZipcodeHouseNumber(query)
      const phoneNumber = CustomerSearchHelper.filterPhoneNumber(query)

      if (poIdOrKVKNumber) {
        searchParams = {
          poIdOrCoC: poIdOrKVKNumber,
        }
      } else if (kvkLocationNumber) {
        searchParams = {
          chamberOfCommerceEstablishment: kvkLocationNumber,
        }
      } else if (zipcode) {
        searchParams = zipcode
      } else if (phoneNumber) {
        searchParams = {
          phone: phoneNumber,
        }
      }
      return searchParams
    },
    async onSearch(query) {
      this.loading = true
      this.foundCompanies = []
      this.step = 1

      if (!this.dealerSettings.autolineKeyloopEnabled) {
        await this.searchNational(query)
      } else {
        this.foundCompanies = await Companies.searchLeaseCompanies(query)
      }

      this.hasSearched = true
      this.loading = false
    },
    async searchNational(query) {
      let searchParams = this.getSearchNationalParams(query)
      if (!searchParams) {
        this.errorText = this.i18n.t('/Company/CompanyZoekenModal/NationaalZoekenValidatieError')
      } else {
        let foundCompanies = await Companies.searchNationalCompanies(searchParams)

        this.foundCompanies = foundCompanies.map((company) =>
          Companies.convertCompanyToLeaseCompanyDto(company)
        )
      }
    },
    selectCompany(company) {
      const namespace = this.isPurchaseOrderPage ? 'purchaseOrder' : 'quote'

      //First set the lease company
      StoreConnector.dispatch(`${namespace}/setLeaseCompany`, company).then(() => {
        EventBus.$emit(`${namespace}/leaseCompanyUpdated`)
      })

      //Then set the lease reference
      if (namespace === 'quote') {
        let leaseReference = leaseReferenceHelper.generateLeaseReference(
          this.customer,
          this.customerCompany
        )
        StoreConnector.dispatch(`${namespace}/setLeaseReference`, leaseReference)
      }

      this.close()
    },
    showMoreResults() {
      this.amountToShow += 20
    },
  },
}
</script>

<style lang="scss" scoped>
ul {
  @include list-reset;
}
</style>
